
  const TRIGGER = "[aria-haspopup=menu]";
  const ITEM = "[role=menuitem]";

  export default {
    mounted() {
      this.isOpen = false;
      this.closeOnOutsidePress = (event) => {
        if (!this.el.contains(event.target)) this.close(null);
      };

      // Stops the press from moving focus, so a press on the button of an
      // open menu closes it here rather than blurring it shut in Safari.
      this.el.addEventListener("mousedown", (event) => {
        if (event.target.closest(TRIGGER)) event.preventDefault();
      });

      this.el.addEventListener("click", (event) => {
        if (event.target.closest(ITEM)) return this.close(this.trigger());
        if (!event.target.closest(TRIGGER)) return;
        if (this.isOpen) return this.close(this.trigger());
        // Keyboard and assistive-technology presses report no click count.
        this.open(event.detail === 0 ? () => this.items()[0] : () => this.list());
      });

      this.el.addEventListener("keydown", (event) => this.keydown(event));

      this.el.addEventListener("focusout", (event) => {
        const next = event.relatedTarget;
        if (this.isOpen && next && !this.el.contains(next)) this.close(null);
      });
    },

    // A stream reset moves the row, and moving an element drops its focus.
    updated() {
      if (this.isOpen && !this.el.contains(document.activeElement)) this.list().focus();
    },

    destroyed() {
      document.removeEventListener("pointerdown", this.closeOnOutsidePress, true);
    },

    trigger() {
      return this.el.querySelector(TRIGGER);
    },

    list() {
      return this.el.querySelector("[role=menu]");
    },

    items() {
      return [...this.el.querySelectorAll(ITEM)];
    },

    open(focusTarget) {
      this.isOpen = true;
      // Queued before the classes that show the menu, so both land in the
      // same frame and the menu is never drawn on the wrong side.
      this.placeMenu();
      this.js().removeClass(this.el, "dropdown-close");
      this.js().addClass(this.el, "dropdown-open");
      this.js().setAttribute(this.trigger(), "aria-expanded", "true");
      document.addEventListener("pointerdown", this.closeOnOutsidePress, true);
      // LiveView applies the class change in the next animation frame, and
      // the menu cannot take focus while it is still hidden. Frame
      // callbacks run in the order they were queued, so this one runs after.
      requestAnimationFrame(() => focusTarget()?.focus());
    },

    // The side is chosen again on every open, because the row may have
    // moved. It is left in place on close: the menu fades out where it was.
    placeMenu() {
      const room = this.roomAround();
      const upward = this.menuHeight() > room.below && room.above > room.below;
      if (upward) this.js().addClass(this.el, "dropdown-top");
      else this.js().removeClass(this.el, "dropdown-top");
    },

    // The shut menu is display: none, so it is shown for this one
    // measurement. Its shut styles keep it transparent, and it is hidden
    // again before anything is drawn.
    menuHeight() {
      const list = this.list();
      list.style.display = "flex";
      const height = list.offsetHeight;
      list.style.display = "";
      return height;
    },

    // The space above and below the button inside the nearest enclosing
    // element that clips or scrolls, cut to the window.
    roomAround() {
      const box = this.scrollBox()?.getBoundingClientRect();
      const top = Math.max(box?.top ?? 0, 0);
      const bottom = Math.min(box?.bottom ?? window.innerHeight, window.innerHeight);
      const own = this.el.getBoundingClientRect();
      return { above: own.top - top, below: bottom - own.bottom };
    },

    scrollBox() {
      for (let node = this.el.parentElement; node && node !== document.body; node = node.parentElement) {
        const { overflowX, overflowY } = getComputedStyle(node);
        if (overflowX !== "visible" || overflowY !== "visible") return node;
      }
      return null;
    },

    close(returnFocusTo) {
      this.isOpen = false;
      this.js().removeClass(this.el, "dropdown-open");
      this.js().addClass(this.el, "dropdown-close");
      this.js().setAttribute(this.trigger(), "aria-expanded", "false");
      document.removeEventListener("pointerdown", this.closeOnOutsidePress, true);
      returnFocusTo?.focus();
    },

    keydown(event) {
      if (!this.isOpen) {
        if (event.key === "ArrowDown" && event.target.closest(TRIGGER)) {
          event.preventDefault();
          this.open(() => this.items()[0]);
        }
        return;
      }

      if (event.key === "Escape") {
        event.preventDefault();
        // A dialog or page shortcut behind the menu must not also close.
        event.stopPropagation();
        this.close(this.trigger());
      } else if (event.key === "Tab") {
        // Tab then moves on from the button, as it would with the menu shut.
        this.close(this.trigger());
      } else if (["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
        event.preventDefault();
        this.moveFocus(event.key);
      }
    },

    moveFocus(key) {
      const items = this.items();
      const last = items.length - 1;
      const current = items.indexOf(document.activeElement);
      const targets = {
        ArrowDown: current < 0 ? 0 : Math.min(current + 1, last),
        ArrowUp: current < 0 ? last : Math.max(current - 1, 0),
        Home: 0,
        End: last,
      };
      items[targets[key]]?.focus();
    },
  };
